/**
 * MarketplaceTracker.ts
 *
 * Self-contained analytics tracker for the Marketplace frontend.
 * Implements POST /api/v1/marketplace/analytics/track/ as specified
 * in the Marketplace Analytics FE Guide.
 *
 * Design:
 *  - anonymous_id  → localStorage  "mp_anon"   (one UUID per device/browser, forever)
 *  - session_id    → sessionStorage "mp_sess"   (new UUID each browser session)
 *  - session start → sessionStorage "mp_sess_t" (ISO timestamp, for duration_ms)
 *  - In-memory event queue, flushed in ≤100-event batches
 *  - Bearer token sourced via project-canonical getTokens() (Redux → localStorage fallback)
 *  - Impression de-dupe: per-session Set keyed by `${sessionId}-${entityId}`
 *  - Auto-flush: every 5 s + on tab hide (fires session_end first, then flush)
 */

import { getHostUrl } from "../Redux/ApiCalls/getHostUrl";
import { getTokens } from "../Redux/ApiCalls/AuthorizedApi";

const MP_TRACK_PATH = "/api/v1/marketplace/analytics/track/";

// ---------------------------------------------------------------------------
// ID helpers
// ---------------------------------------------------------------------------

function anonId(): string {
  let id = localStorage.getItem("mp_anon");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("mp_anon", id);
  }
  return id;
}

function sessionId(): string {
  let id = sessionStorage.getItem("mp_sess");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("mp_sess", id);
    // Record session start time
    sessionStorage.setItem("mp_sess_t", new Date().toISOString());
  }
  return id;
}

function sessionStartIso(): string {
  return sessionStorage.getItem("mp_sess_t") || new Date().toISOString();
}

function sessionDurationMs(): number {
  const start = new Date(sessionStartIso()).getTime();
  return Date.now() - start;
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

interface MpEvent {
  event_name: string;
  session_id: string;
  anonymous_id: string;
  platform: "web";
  occurred_at: string;
  [key: string]: any;
}

const queue: MpEvent[] = [];

/**
 * Queue a single marketplace analytics event.
 * Fire-and-forget — never throws.
 */
export function mpTrack(event_name: string, extra: Record<string, any> = {}): void {
  try {
    queue.push({
      event_name,
      session_id: sessionId(),
      anonymous_id: anonId(),
      platform: "web",
      occurred_at: new Date().toISOString(),
      ...extra,
    });
  } catch {
    // Silently swallow — tracking must never break the UI
  }
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

export async function flushMpQueue(): Promise<void> {
  if (queue.length === 0) return;

  // Drain up to 100 at a time
  const events = queue.splice(0, 100);

  const trackUrl = `${getHostUrl()}${MP_TRACK_PATH}`;
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  try {
    // Use project-canonical token accessor: Redux store first, localStorage fallback
    const { accessToken } = getTokens();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    await fetch(trackUrl, {
      method: "POST",
      headers,
      body: JSON.stringify({ events }),
      // keepalive ensures the request survives page unload (visibilitychange → hidden)
      keepalive: true,
    });
  } catch {
    // Never block or alert on tracking failures
  }
}

// ---------------------------------------------------------------------------
// Impression de-duplication
// ---------------------------------------------------------------------------

/**
 * Per-session set of already-tracked listing impression keys.
 * Key format: `${listingId}` — unique per session because session_id rotates.
 * Prevents duplicate firing when the user scrolls the same card in/out of view.
 */
const _trackedImpressions = new Set<string>();

/**
 * Track a `listing_impression` event at most once per session per entity.
 * Safe to call from IntersectionObserver callbacks without de-dupe logic in the component.
 */
export function trackImpressionOnce(
  entityId: string,
  extra: Record<string, any> = {}
): void {
  if (_trackedImpressions.has(entityId)) return;
  _trackedImpressions.add(entityId);
  mpTrack("listing_impression", {
    entity_type: "listing",
    entity_id: entityId,
    ...extra,
  });
}

// ---------------------------------------------------------------------------
// Session end helper
// ---------------------------------------------------------------------------

/**
 * Queue a `marketplace.session_end` event with the computed duration_ms.
 * Call this explicitly when the user leaves the marketplace (visibilitychange,
 * navigation away from /marketplace, etc.).
 */
export function trackSessionEnd(): void {
  mpTrack("marketplace.session_end", {
    duration_ms: sessionDurationMs(),
  });
}

// ---------------------------------------------------------------------------
// Auto-flush listeners (registered once on module import)
// ---------------------------------------------------------------------------

// Flush every 5 seconds
setInterval(flushMpQueue, 5_000);

// On tab hide: queue session_end first, then flush synchronously via keepalive fetch
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    trackSessionEnd();
    flushMpQueue();
  }
});
